import React from 'react';
import {
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { BadgeDollarSign } from 'lucide-react';
import type { EmployeeType } from '../../services/api/models/EmployeeType';
import { EMPLOYEE_TYPE_OPTIONS } from '../../constants/employeeTypes';
import type { EmployeeTypeRow } from './useEmployeeTypes';

const NONE_VALUE = '__none__';

type EmployeeTypeSectionProps = {
  rows: EmployeeTypeRow[];
  invalidClinicIds: Set<string>;
  onChangeType: (clinicId: string, employeeType: EmployeeType | null) => void;
  onChangeOther: (clinicId: string, text: string) => void;
};

export const EmployeeTypeSection: React.FC<EmployeeTypeSectionProps> =
  React.memo(function EmployeeTypeSection({
    rows,
    invalidClinicIds,
    onChangeType,
    onChangeOther,
  }) {
    if (rows.length === 0) {
      return null;
    }

    return (
      <div>
        <Separator className='mb-4' />
        <div className='space-y-3'>
          <div className='flex items-center gap-2'>
            <BadgeDollarSign className='h-4 w-4 text-muted-foreground' />
            <h3 className='text-sm font-semibold'>Employee Type</h3>
          </div>
          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='h-8 py-1.5 align-middle text-xs'>
                    Clinic
                  </TableHead>
                  <TableHead className='h-8 w-56 py-1.5 align-middle text-xs'>
                    Employee Type
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow key={row.clinic_id}>
                    <TableCell className='py-1.5 align-middle'>
                      <Label className='min-w-0 truncate text-sm font-medium'>
                        {row.clinic_name}
                      </Label>
                    </TableCell>
                    <TableCell className='py-1.5 align-middle'>
                      <div className='space-y-1.5'>
                        <Select
                          value={row.employee_type ?? NONE_VALUE}
                          onValueChange={value =>
                            onChangeType(
                              row.clinic_id,
                              value === NONE_VALUE
                                ? null
                                : (value as EmployeeType)
                            )
                          }
                        >
                          <SelectTrigger selectSize='sm'>
                            <SelectValue placeholder='Not set' />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE_VALUE}>Not set</SelectItem>
                            {EMPLOYEE_TYPE_OPTIONS.map(option => (
                              <SelectItem
                                key={option.value}
                                value={option.value}
                              >
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {row.employee_type === 'OTHER' && (
                          <>
                            <Input
                              inputSize='sm'
                              placeholder='Enter employee type'
                              value={row.employee_type_other}
                              onChange={e =>
                                onChangeOther(row.clinic_id, e.target.value)
                              }
                            />
                            {invalidClinicIds.has(row.clinic_id) && (
                              <p className='text-xs text-destructive'>
                                Required when Other is selected
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    );
  });
